import { Button } from 'antd';
import { MonitorX } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { appLogger } from '@/utils/logger';

export interface BoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
  errorMessage?: string;
  redirectTo?: string; // Nueva propiedad para redirección
  retryButtonText?: string; // Nueva propiedad para texto del botón
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<BoundaryProps, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    appLogger.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private readonly handleRetry = () => {
    if (this.props.onRetry) {
      this.props.onRetry(); // Ejecutar la acción personalizada primero
    }
    if (this.props.redirectTo) {
      window.location.href = this.props.redirectTo; // Redirigir si se especifica
    } else {
      window.location.reload(); // Refrescar la página si no hay redirección
    }
    this.setState({ hasError: false, error: undefined }); // Reiniciar estado de error
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-1 w-full h-full min-h-[300px] items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-10 py-8">
            <div className="flex items-center justify-center border rounded-full w-[120px] h-[120px] p-2 bg-[#F8FBFC] text-gray-300">
              <MonitorX className="text-6xl" />
            </div>
            <div className="flex flex-col items-center gap-3 mt-4">
              <p
                className="font-medium"
                style={{
                  color: '#000',
                  textAlign: 'center',
                  fontSize: 18,
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: 'normal',
                }}
              >
                {this.props.errorMessage || 'Something went wrong'}
              </p>
              <p
                style={{
                  color: '#9E9E9E',
                  textAlign: 'center',
                  fontSize: 15,
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                Looks like something went wrong. Please refresh and try again.
              </p>
              <Button
                className="bg-primary-gold hover:!bg-gold-darker"
                style={{
                  display: 'flex',
                  height: 50,
                  maxWidth: 139,
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                }}
                type="primary"
                onClick={this.handleRetry}
              >
                {this.props.retryButtonText || 'Reload Page'} {/* Usar texto personalizado si se proporciona */}
              </Button>
              <div
                style={{
                  color: '#9E9E9E',
                  textAlign: 'center',
                  fontSize: 12,
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                If the problem persists, try clearing your browser cache.
              </div>
              {import.meta.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-sm text-gray-600">Error Details (Development)</summary>
                  <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto">{this.state.error.stack}</pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
